import { Icon } from "@indigo-components";
import {
  type ChatResponseDocumentItem,
  ChatResponseDocumentList,
} from "@indigo-components/components/Chat";
import type { FC } from "react";
import type {
  DocumentCard,
  DocumentListBlock as DocumentListBlockData,
} from "~/pydantic_models/services/models/chat_blocks";
import type { ChatBlockRendererProps } from "./types";

/**
 * `document_list` → `ChatResponseDocumentList`.
 *
 * The payload is display-shaped on purpose (built by
 * `services/agent/tools/render_document_list.py`): names and subtitles
 * arrive finished, so nothing is re-derived here. A row opens the file in
 * the Case Documents inline viewer through the shared chatbot link
 * context — the same consumer the `indigo://appointment-doc` markdown
 * links already use.
 */

/** Icon hints the backend may send; anything else falls back to a plain
 * file glyph rather than rendering nothing. */
const DOCUMENT_ICONS: Record<string, "pdf" | "file"> = {
  pdf: "pdf",
  file: "file",
};

/**
 * Network payload — validate the shape we actually read before trusting it.
 *
 * The types come from `services/models/chat_blocks.py` via
 * `generate_ts_models.sh`, so a renamed or retyped field on the backend
 * breaks the build here instead of silently emptying a row. The runtime
 * check stays because the payload still arrives as untyped JSON: an older
 * row or a newer backend can carry a shape this bundle's types don't
 * describe.
 */
function parseDocumentList(
  data: Record<string, unknown> | null | undefined,
): DocumentListBlockData | null {
  if (!data || !Array.isArray(data.documents)) return null;
  const documents: DocumentCard[] = [];
  for (const raw of data.documents) {
    const card = raw as Partial<DocumentCard> | null;
    if (
      !card ||
      typeof card.id !== "string" ||
      typeof card.title !== "string"
    ) {
      return null;
    }
    documents.push(card as DocumentCard);
  }
  return {
    header: typeof data.header === "string" ? data.header : null,
    documents,
  };
}

export const DocumentListBlock: FC<ChatBlockRendererProps> = ({
  block,
  ctx,
  fallback,
}) => {
  const parsed = parseDocumentList(block.data);
  if (!parsed || parsed.documents.length === 0) return <>{fallback}</>;

  const items: ChatResponseDocumentItem[] = parsed.documents.map((card) => {
    const iconName = DOCUMENT_ICONS[card.icon ?? "file"] ?? "file";
    // Every case document opens. The sidebar sends sub-rosa to the video
    // player, any PDF to its inline viewer, and the rest to a
    // browser-rendered tab or a download — and resolves a file it doesn't
    // hold locally on its own, so nothing here needs to know which
    // appointment owns it.
    // `page` is honoured by the one page-aware viewer and ignored
    // elsewhere, so the scheme's required page defaults to the first.
    const openable = !!ctx.onRequestAppointmentDocument;
    return {
      id: card.id,
      icon: <Icon name={iconName} size="m" />,
      title: card.title,
      subtitle: card.subtitle ?? undefined,
      onClick: openable
        ? () =>
            ctx.onRequestAppointmentDocument?.({
              uploadedFileId: card.id,
              page: card.page ?? 1,
            })
        : undefined,
    };
  });

  return (
    <ChatResponseDocumentList
      header={parsed.header ?? undefined}
      documents={items}
      data-testid="chat-document-list"
    />
  );
};
